import { Component, OnDestroy, OnInit } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  Validators,
  ReactiveFormsModule,
  AbstractControl,
  ValidationErrors,
  FormsModule,
} from '@angular/forms';
import { UserService } from '../../../core/services/user.service';
import { RouterModule } from '@angular/router';
import { CommonModule } from '@angular/common';
import { TPurchasingCodeDto } from '../../../core/interfaces/tpurchasing-code.model';
import { User } from '../../../core/interfaces/user';
import { HttpUtilityService } from '../../../core/services/http.utility.service';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzTableModule } from 'ng-zorro-antd/table';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzGridModule } from 'ng-zorro-antd/grid';
import { UserApiService } from '../../../core/services/user-api.service';
import { HttpErrorResponse } from '@angular/common/http';
import { Subscription } from 'rxjs';
import { constantTxt } from '../../../core/constants/constants';

@Component({
  selector: 'app-manage-user',
  templateUrl: './manage-user.component.html',
  styleUrls: ['./manage-user.component.less'],
  imports: [
    ReactiveFormsModule,
    FormsModule,
    CommonModule,
    RouterModule,
    NzButtonModule,
    NzTableModule,
    NzSelectModule,
    NzInputModule,
    NzGridModule,
  ],
  providers: [HttpUtilityService],
})
export class ManageUserComponent implements OnInit, OnDestroy {
  users: User[] = [];
  filteredUsers: User[] = [];
  searchText: string = '';
  purchasingOffices: { id: string; name: string }[] = [];
  selectedUser: User | null = null;
  userForm!: FormGroup;
  showAddUser: boolean = false;
  showUserPopup: boolean = false;
  isEditMode: boolean = false;
  isInputFocused: boolean = false;
  pageSize = 100;
  pageNumber = 1;
  customNoDataMessage = constantTxt.noDataFound;
  getOfficesListSubscription!: Subscription;
  getUsersSubscription!: Subscription;
  addUserSubscription!: Subscription;
  searchUserByEmailSubscription!: Subscription;
  deleteUserSubscription!: Subscription;
  updateUserSubscription!: Subscription;

  constructor(
    private readonly userService: UserService,
    private readonly fb: FormBuilder,
    private readonly userApiService: UserApiService
  ) {}

  ngOnInit(): void {
    this.initializeForm();
    this.loadPurchasingCodes();
    this.loadUsers();
  }

  initializeForm() {
    this.userForm = this.fb.group({
      status: ['true', Validators.required],
      firstName: ['', Validators.required],
      lastName: ['', Validators.required],
      email: ['', [Validators.required, Validators.email]],
      purchasingOffice: ['', Validators.required],
      userType: this.fb.group(
        {
          systemAdmin: [false],
          leadBuyer: [false],
          buyer: [false],
        },
        { validators: this.atLeastOneCheckboxCheckedValidator }
      ),
    });
  }

  atLeastOneCheckboxCheckedValidator(
    control: AbstractControl
  ): ValidationErrors | null {
    const controls = control.value;
    const isAnyChecked = Object.values(controls).some(
      (value) => value === true
    );
    return isAnyChecked ? null : { atLeastOneRequired: true };
  }

  loadPurchasingCodes() {
    this.getOfficesListSubscription = this.userApiService
      .getOfficesList()
      .subscribe({
        next: (response) => {
          const data = response.body;
          if (data) {
            this.purchasingOffices = data
              .filter((x) => x.purchasingStatus)
              .map((o: any) => ({
                id: o.purchasingId?.toString(),
                name: o.purchasingName,
              }));
          }
        },
        error: (error) => {
          console.log(
            'Error occured while loading purchasing code. Error: ',
            error
          );
        },
      });
  }

  loadUsers(): void {
    this.getUsersSubscription = this.userService.getUsers().subscribe({
      next: (response: any) => {
        let data = response.body;
        if (data) {
          this.users = data.map((u: any) => ({
            id: u.userId ?? u.UserId,
            status: u.userStatus === 'A' || u.UserStatus === 'A',
            userName: u.userName,
            firstName: u.userFirstName ?? u.UserFirstName,
            lastName: u.userLastName ?? u.UserLastName,
            email: u.userEmail ?? u.UserEmail,
            purchasingOffice: u.userPurchasingOffice ?? u.UserPurchasingOffice,
            userType: {
              systemAdmin:
                u.userTypeSysAdmin === '1' ||
                u.userTypeSysAdmin === true ||
                u.UserTypeSysAdmin === '1' ||
                u.UserTypeSysAdmin === true,
              leadBuyer:
                u.userTypeLead === '1' ||
                u.userTypeLead === true ||
                u.UserTypeLead === '1' ||
                u.UserTypeLead === true,
              buyer:
                u.userTypeBuyer === '1' ||
                u.userTypeBuyer === true ||
                u.UserTypeBuyer === '1' ||
                u.UserTypeBuyer === true,
            },
            createdDate: u.userCreateDate ?? u.UserCreateDate,
            userCreateDate: u.userCreateDate ?? u.UserCreateDate,
            userCreateBy: u.userCreateBy ?? u.UserCreateBy,
            userUpdateDate: u.userUpdateDate ?? u.UserUpdateDate,
            userUpdateBy: u.userUpdateBy ?? u.UserUpdateBy,
          }));
          this.filteredUsers = [...this.users];
        }
      },
      error: (error) => {
        console.log('Error occured while fetching users. Error: ', error);
      },
    });
  }

  onSearchUser(): void {
    const search = this.searchText.trim().toLowerCase();
    if (!search) {
      this.filteredUsers = [...this.users];
      return;
    }
    this.filteredUsers = this.users.filter((user) => {
      const name = `${user.firstName} ${user.lastName}`.toLowerCase();
      const email = user.email?.toLowerCase() || '';
      const office = (user.purchasingOffice || '').toLowerCase();
      return (
        name.includes(search) ||
        email.includes(search) ||
        office.includes(search)
      );
    });
  }

  // Open Edit Form
  editUserDetails(user: User): void {
    this.showUserPopup = true;
    this.isEditMode = true;
    this.userForm.controls['email'].disable();
    this.selectedUser = { ...user };
    // Find the office id by name for the dropdown (match by id or name)
    const office = this.purchasingOffices.find(
      (o) => o.id == user.purchasingOffice || o.name === user.purchasingOffice
    );
    this.userForm.patchValue({
      status: user.status ? 'true' : 'false',
      firstName: user.firstName,
      lastName: user.lastName,
      email: user.email,
      purchasingOffice: office ? office.id : '',
      userType: {
        systemAdmin: user.userType.systemAdmin,
        leadBuyer: user.userType.leadBuyer,
        buyer: user.userType.buyer,
      },
    });
  }

  // Adds New User
  addUser(): void {
    if (this.userForm.valid) {
      const formValue = this.userForm.value;

      const selectedOffice = this.purchasingOffices.find(
        (o) => o.id == formValue.purchasingOffice
      );

      const officeName = selectedOffice ? selectedOffice.name : '';
      const newUser = {
        UserStatus: formValue.status == 'true' ? 'A' : 'I',
        UserFirstName: formValue.firstName,
        UserLastName: formValue.lastName,
        UserEmail: formValue.email,
        UserPurchasingOffice: officeName,
        UserTypeSysAdmin: formValue.userType.systemAdmin ? '1' : '0',
        UserTypeLead: formValue.userType.leadBuyer ? '1' : '0',
        UserTypeBuyer: formValue.userType.buyer ? '1' : '0',
        UserCreateDate: new Date(),
        UserCreateBy: sessionStorage.getItem('UserEmail') || '',
      };
      this.addUserSubscription = this.userService.addUser(newUser).subscribe({
        next: () => {
          this.loadUsers();
          alert('User added successfully!');
          this.closePopup();
        },
        error: (error: HttpErrorResponse) => {
          console.error('Error adding user:', error.error);
          if (
            error.error.toLowerCase() === 'user already exists with this mail'
          ) {
            alert('User name does exist, could not use this name.');
          } else {
            alert('Failed to add user. Please try again.');
          }
        },
      });
    } else {
      this.userForm.markAllAsTouched();
      alert('Please fill all required fields.');
    }
  }

  // Saves Edited Data
  updateUserDetails(): void {
    if (this.selectedUser && this.userForm.valid) {
      const formValue = this.userForm.getRawValue();
      const selectedOffice = this.purchasingOffices.find(
        (o) => o.id == formValue.purchasingOffice
      );
      const officeName = selectedOffice ? selectedOffice.name : '';
      const updatedUser = {
        UserId: this.selectedUser.id,
        UserStatus: formValue.status == 'true' ? 'A' : 'I',
        UserEmail: formValue.email,
        UserPurchasingOffice: officeName,
        UserTypeSysAdmin: formValue.userType.systemAdmin ? '1' : '0',
        UserTypeLead: formValue.userType.leadBuyer ? '1' : '0',
        UserTypeBuyer: formValue.userType.buyer ? '1' : '0',
        UserUpdateDate: new Date(),
        UserUpdateBy: sessionStorage.getItem('UserEmail') || '',
      };
      this.updateUserSubscription = this.userService
        .updateUser(this.selectedUser.id, updatedUser)
        .subscribe(() => {
          this.loadUsers();
          this.closePopup();
          window.location.href = '/admin/manage-user';
          alert('User updated successfully!');
        });
    } else {
      this.userForm.markAllAsTouched();
      alert('Please fill all required fields.');
    }
  }

  deleteUser(): void {
    if (confirm('Are you sure to delete this user?')) {
      let id = this.selectedUser?.id ?? 0;
      this.deleteUserSubscription = this.userService
        .deleteUser(id)
        .subscribe(() => {
          this.loadUsers();
          this.selectedUser = null;
          this.closePopup();
        });
    }
  }

  onPageSizeChange(pageSize: number): void {
    this.pageSize = pageSize;
    this.pageNumber = 1;
  }

  onPageIndexChange(pageIndex: number): void {
    this.pageNumber = pageIndex;
  }

  get addUserTypeGroup(): FormGroup {
    return this.userForm.get('userType') as FormGroup;
  }
  get editUserTypeGroup(): FormGroup {
    return this.userForm.get('userType') as FormGroup;
  }

  onFocus() {
    this.isInputFocused = true;
  }

  getDetailsByEmail() {
    const email = this.userForm.value.email;
    const emailError = this.userForm.get('email')?.errors?.['email'];
    if (email && !emailError && this.isInputFocused) {
      this.isInputFocused = false;
      this.searchUserByEmailSubscription = this.userApiService
        .searchUserByEmail(email)
        .subscribe({
          next: (res) => {
            let data = res.body;
            if (data) {
              this.userForm.patchValue({
                firstName: data.preferredFirstName,
                lastName: data.preferredLastName,
                email: data.emailAddress,
              });
            }
          },
          error: (error) => {
            console.log('Error fetching user by email. Error: ', error);
          },
        });
    }
  }

  userRoles(user: any) {
    let rolesArr: string[] = [];
    if (user.userType.systemAdmin) {
      rolesArr.push('System Admin');
    }
    if (user.userType.leadBuyer) {
      rolesArr.push('Lead Buyer');
    }
    if (user.userType.buyer) {
      rolesArr.push('Buyer');
    }

    return rolesArr.join(' / ');
  }

  // Opens Add User Popup
  addNewUser() {
    this.userForm.controls['email'].enable();
    this.showUserPopup = true;
  }

  closePopup() {
    this.userForm.reset({
      status: true,
      userType: { systemAdmin: false, buyer: false },
    });
    this.showUserPopup = false;
    this.isEditMode = false;
  }

  ngOnDestroy(): void {
    if (this.getOfficesListSubscription) {
      this.getOfficesListSubscription.unsubscribe();
    }

    if (this.getUsersSubscription) {
      this.getUsersSubscription.unsubscribe();
    }

    if (this.addUserSubscription) {
      this.addUserSubscription.unsubscribe();
    }

    if (this.searchUserByEmailSubscription) {
      this.searchUserByEmailSubscription.unsubscribe();
    }

    if (this.deleteUserSubscription) {
      this.deleteUserSubscription.unsubscribe();
    }

    if (this.updateUserSubscription) {
      this.updateUserSubscription.unsubscribe();
    }
  }
}
